import { get, pick } from 'lodash/fp';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, string, eq, number, option, record, ord, nonEmptyArray } from 'fp-ts';
import { getStringArrayProp, getStringProp } from '@mews/fp';
import { createAction } from '@mews/framework';
import { ResourceCategoryType } from '@mews-commander/legacy-data-structures';
import { ApplicationState, Option } from '@mews-commander/types';
import { ResourceCategory } from '@mews-commander/data-access';
import { EnumValue } from '@mews/enumify';
import { createOptionsSelector, mapId } from '@mews-commander/utils';
import parentSelector from '../selector';
import prefix from '../prefix';
import { orderOrderableEntities } from '../utils/orderableEntities';
import {
    createBaseEntitySelectors,
    createScopedEntitiesReducer,
    createScopedBaseEntitySelectors,
    Entities,
} from './utils';

export const NAME = 'resourceCategories';

export const RESOURCE_CATEGORIES_UPDATE = prefix('RESOURCE_CATEGORIES_UPDATE');

export const [getResourceCategories, getResourceCategoriesByIds, getResourceCategoriesByOwnerId, getResourceCategory] =
    createScopedBaseEntitySelectors<ApplicationState, ResourceCategory, typeof NAME, 'serviceId'>(
        parentSelector,
        NAME,
        'serviceId'
    );
export const [getRootResourceCategories] = createBaseEntitySelectors<
    ApplicationState,
    Entities<ResourceCategory>,
    typeof NAME
>(parentSelector, NAME);

export const setResourceCategories = (entities: ResourceCategory[], scope: string) =>
    createAction(RESOURCE_CATEGORIES_UPDATE, entities, { scope });

export const getResourceCategoryNameVariants = createSelector(getResourceCategory, pick(['name', 'shortName']));
export const getResourceCategoryName = createSelector(getResourceCategory, get('name'));
export const getResourceCategoryShortName = createSelector(getResourceCategory, get('shortName'));
export const getResourceCategoryCapacity = createSelector(getResourceCategory, get('capacity'));
export const getResourceCategoryDescription = createSelector(getResourceCategory, get('description'));

export const resourceCategoriesReducer = createScopedEntitiesReducer({
    UPDATE_SUCCESS: RESOURCE_CATEGORIES_UPDATE,
});

/**
 * @deprecated creates new record each time
 */
export const getSortedResourceCategories = flow(getResourceCategories, orderOrderableEntities);

export const getSortedResourceCategoryIds = createSelector(getSortedResourceCategories, mapId);

export const getResourceCategoriesByServiceIds = createSelector(
    getStringArrayProp('serviceIds'),
    getRootResourceCategories,
    (serviceIds, rootResourceCategories) =>
        pipe(
            serviceIds,
            array.reduce<string, Entities<ResourceCategory>>({}, (acc, serviceId) =>
                pipe(
                    rootResourceCategories,
                    record.lookup(serviceId),
                    option.match(
                        () => acc,
                        (serviceResourceCategories) => ({ ...serviceResourceCategories, ...acc })
                    )
                )
            )
        )
);

const getResourceCategoriesGroupByServiceId = createSelector(
    getResourceCategoriesByServiceIds,
    flow(
        Object.values<ResourceCategory>,
        nonEmptyArray.groupBy(({ serviceId }) => serviceId)
    )
);

export const getResourceCategoryIdsGroupByServiceId = createSelector(
    getResourceCategoriesGroupByServiceId,
    record.map(array.map((resourceCategory) => resourceCategory.id))
);

const sortByEnumValue = pipe(
    number.Ord,
    ord.contramap(({ value }: EnumValue<string, number>) => value)
);

const getResourceCategoryTypesFromResourceCategories = flow(
    array.map(({ type }: ResourceCategory) => ResourceCategoryType.getByName(type)),
    array.uniq(
        eq.struct({
            name: string.Eq,
            value: number.Eq,
        })
    ),
    array.sortBy([sortByEnumValue])
);

export const getResourceCategoryTypesByServiceIds = flow(
    getResourceCategoriesByServiceIds,
    Object.values<ResourceCategory>,
    getResourceCategoryTypesFromResourceCategories
);

export const getResourceCategoriesOptions: (state: ApplicationState, params: { serviceId: string }) => Option[] =
    createOptionsSelector(getSortedResourceCategories);

export const getResourceCategoryIds = createSelector(getSortedResourceCategories, mapId);

export const getSortedResourceCategorySelectOptionsByIds = createOptionsSelector(getResourceCategoriesByIds, {
    sortFunction: orderOrderableEntities,
});

export const getResourceCategoryTypesByResourceCategoryIds = createSelector(
    getResourceCategoriesByIds,
    getResourceCategoryTypesFromResourceCategories
);

export const getResourceCategoriesByResourceCategoryTypeName = createSelector(
    getStringProp('resourceCategoryTypeName'),
    getSortedResourceCategories,
    (resourceCategoryTypeName, serviceResourceCategories) =>
        pipe(
            // @ts-expect-error
            serviceResourceCategories,
            array.filterMap((resourceCategory: ResourceCategory) =>
                resourceCategory.type === resourceCategoryTypeName ? option.some(resourceCategory) : option.none
            )
        )
);

export const getResourceCategoryIdsByResourceCategoryTypeName = flow(
    getResourceCategoriesByResourceCategoryTypeName,
    array.map(({ id }) => id)
);
